import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useState, useEffect, createElement, Fragment, cloneElement, useCallback, forwardRef } from 'react';
import cn from 'classnames';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { IconButton } from '../IconButton/IconButton.js';
import { motion } from 'framer-motion';
import { useGaudiLocalization } from '../Provider/Provider.js';
import { useDialogState, DialogDisclosure, DialogBackdrop, Dialog } from 'reakit';
import { useDraggable } from '../../utils/hooks/useDraggable.js';

var _excluded = ["children", "disclosure", "disclosureTooltip", "showCloseButton", "visible", "drawerDefaultVisible", "onHide", "drawer", "extra", "hideOnClickOutside", "draggable", "size"],
    _excluded2 = ["children", "className", "centered"],
    _excluded3 = ["children", "className"],
    _excluded4 = ["children", "className"],
    _excluded5 = ["children", "className", "visible"],
    _excluded6 = ["children", "className"];

var VisibilityWrapper = function VisibilityWrapper(_ref) {
  var children = _ref.children,
      disclosureRef = _ref.disclosureRef;
  useEffect(function () {
    return function () {
      if (disclosureRef && disclosureRef.current) {
        disclosureRef.current.focus();
      }
    };
  }, []);
  return children;
};

var Modal = function Modal(props) {
  var children = props.children,
      disclosure = props.disclosure,
      disclosureTooltip = props.disclosureTooltip,
      _props$showCloseButto = props.showCloseButton,
      showCloseButton = _props$showCloseButto === void 0 ? true : _props$showCloseButto,
      _props$visible = props.visible,
      visible = _props$visible === void 0 ? false : _props$visible,
      _props$drawerDefaultV = props.drawerDefaultVisible,
      drawerDefaultVisible = _props$drawerDefaultV === void 0 ? false : _props$drawerDefaultV,
      onHide = props.onHide,
      drawer = props.drawer,
      extra = props.extra,
      _props$hideOnClickOut = props.hideOnClickOutside,
      hideOnClickOutside = _props$hideOnClickOut === void 0 ? false : _props$hideOnClickOut,
      _props$draggable = props.draggable,
      draggable = _props$draggable === void 0 ? false : _props$draggable,
      _props$size = props.size,
      size = _props$size === void 0 ? 'medium' : _props$size,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded);

  var defaultDisclosureRef = useRef(typeof document !== 'undefined' ? document.body : null);
  var backdropRef = useRef(null);
  var disclosureRef = useRef(null);
  var modalRef = useRef(null);

  var _useDraggable = useDraggable(modalRef),
      position = _useDraggable.position,
      dragHandleProps = _useDraggable.handleProps;

  var _useGaudiLocalization = useGaudiLocalization(),
      texts = _useGaudiLocalization.texts;

  var dialog = useDialogState({
    visible: visible
  });

  var _React$useState = useState(drawerDefaultVisible),
      drawerVisible = _React$useState[0],
      setDrawerVisible = _React$useState[1]; // remember if modal was open before (hint: in the beginning it wasn't!)


  var wasModalVisible = useRef(dialog.visible); // we want to know how the dialog was closed! 'other' means backdrop, but maybe not...

  var closeReasonRef = useRef('other'); // detect closing of the modal by comparing visibility between states

  useEffect(function () {
    if (wasModalVisible.current && !dialog.visible && onHide) {
      onHide(closeReasonRef.current);
    }

    if (!dialog.visible) {
      closeReasonRef.current = 'other';
      setDrawerVisible(drawerDefaultVisible);
    }

    wasModalVisible.current = dialog.visible;
  }, [wasModalVisible, dialog.visible]);

  function renderPropHide() {
    closeReasonRef.current = 'renderprop';
    dialog.hide();
  }

  var ContentWrapper = Fragment;
  var wrapperProps = {};

  if (!!drawer) {
    ContentWrapper = 'div';
    wrapperProps.className = 'yt-modal__drawer-wrapper';
  }

  var renderProps = _extends({}, dialog, {
    hide: renderPropHide,
    drawer: {
      visible: !!drawer && drawerVisible,
      toggle: function toggle() {
        if (!!drawer) return setDrawerVisible(!drawerVisible);
      },
      show: function show() {
        if (!!drawer) return setDrawerVisible(true);
      },
      hide: function hide() {
        if (!!drawer) return setDrawerVisible(false);
      }
    }
  });

  useEffect(function () {
    var parentBackdrop;

    if (backdropRef.current) {
      var parentPortalsParent = backdropRef.current.parentElement && backdropRef.current.parentElement.parentElement;

      if (parentPortalsParent && parentPortalsParent.classList.contains('__reakit-portal')) {
        parentBackdrop = Array.from(parentPortalsParent.children).find(function (x) {
          return x.classList.contains('yt-backdrop');
        });

        if (parentBackdrop) {
          if (dialog.visible) {
            parentBackdrop.classList.add('yt-forcibly__hidden');
          } else {
            parentBackdrop.classList.remove('yt-forcibly__hidden');
          }
        }
      }
    }

    return function () {
      if (parentBackdrop) {
        parentBackdrop.classList.remove('yt-forcibly__hidden');
      }
    };
  }, [dialog.visible]);
  var modalWidths = {
    dialog: 'w-128 text-center justify-center',
    xs: 'w-xs',
    sm: 'w-sm',
    md: 'w-md',
    lg: 'w-lg'
  };
  var className = cn('yt-modal__container', {
    'yt-modal--with-drawer': !!drawer
  }, {
    'yt-modal--with-extra': !!extra
  }, props.className, modalWidths[size]);
  return createElement(Fragment, null, disclosure && createElement(DialogDisclosure, Object.assign({}, dialog, disclosure.props, {
    ref: disclosureRef
  }), function (disclosureProps) {
    var button = cloneElement(disclosure, disclosureProps);
    return disclosureTooltip ? createElement(Tooltip, {
      title: disclosureTooltip
    }, button) : button;
  }), createElement(DialogBackdrop, Object.assign({}, dialog, {
    className: "yt-backdrop",
    ref: backdropRef
  }), createElement(Dialog, Object.assign({}, otherProps, dialog, {
    className: className,
    ref: modalRef,
    unstable_finalFocusRef: disclosure ? undefined : defaultDisclosureRef,
    hideOnClickOutside: hideOnClickOutside,
    style: _extends({}, otherProps.style, {
      left: "calc(50% + " + position.x + "px)",
      top: position.y + "px"
    })
  }), dialog.visible && createElement(VisibilityWrapper, {
    disclosureRef: disclosureRef
  }, createElement(ContentWrapper, Object.assign({}, wrapperProps), createElement("div", {
    className: "yt-modal"
  }, typeof children === 'function' ? children(renderProps) : children), drawer && createElement(Drawer, {
    visible: drawerVisible
  }, typeof drawer === 'function' ? drawer(renderProps) : drawer)), extra && createElement(Extra, null, typeof extra === 'function' ? extra(renderProps) : extra), draggable && createElement("div", Object.assign({}, dragHandleProps, {
    "aria-label": texts.modal.drag,
    className: "yt-modal__drag"
  })), showCloseButton && // Keep the close icon as the last element to receive focus
  createElement(IconButton, {
    appearance: "discrete",
    "aria-label": texts.modal.close,
    className: "yt-modal__close",
    icon: "close",
    onClick: function onClick() {
      closeReasonRef.current = 'close';
      dialog.hide();
    }
  })))));
};
var Header = /*#__PURE__*/forwardRef(function Header(props, ref) {
  var children = props.children,
      className = props.className,
      _props$centered = props.centered,
      centered = _props$centered === void 0 ? true : _props$centered,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded2);

  return createElement("div", Object.assign({}, otherProps, {
    ref: ref,
    className: cn('yt-modal__header', {
      'yt-modal__header--centered': centered
    }, className)
  }), createElement("h2", null, children));
});
var Content = /*#__PURE__*/forwardRef(function Content(props, ref) {
  var children = props.children,
      className = props.className,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded3);

  return createElement("div", Object.assign({}, otherProps, {
    ref: ref,
    className: cn('yt-modal__content', className)
  }), children);
});
var Footer = /*#__PURE__*/forwardRef(function Footer(props, ref) {
  var children = props.children,
      className = props.className,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded4);

  return createElement("div", Object.assign({}, otherProps, {
    ref: ref,
    className: cn('yt-modal__footer', className)
  }), children);
});

var Drawer = function Drawer(props) {
  var children = props.children,
      visible = props.visible,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded5);

  var resetScrollRef = useCallback(function (node) {
    if (node !== null && visible) {
      node.scrollTop = 0;
    }
  }, [visible]);
  return createElement(motion.div, {
    className: "yt-modal__drawer",
    "aria-hidden": !visible,
    variants: {
      visible: {
        x: '99%',
        transition: {
          ease: 'easeOut',
          duration: 0.2
        },
        display: 'block'
      },
      hidden: {
        x: '0%',
        transition: {
          ease: 'easeOut',
          duration: 0.2
        },
        transitionEnd: {
          display: 'none'
        }
      }
    },
    animate: visible ? 'visible' : 'hidden'
  }, createElement("div", Object.assign({}, otherProps, {
    ref: resetScrollRef
  }), children));
};

var Extra = function Extra(props) {
  var children = props.children,
      className = props.className,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded6);

  return createElement("div", Object.assign({}, otherProps, {
    className: cn('yt-modal__extra', className)
  }), children);
};

Modal.Header = Header;
Modal.Footer = Footer;
Modal.Content = Content;

export { Modal };
//# sourceMappingURL=Modal.js.map
